import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Briefcase, GraduationCap, Building2 } from "lucide-react";
import { PageHero, Section, Card } from "@/components/Section";
import { getFormation } from "@/lib/api";
import { IMAGES } from "@/lib/site";

export default function FormationDetail() {
  const { slug } = useParams();
  const [formation, setFormation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    getFormation(slug)
      .then(setFormation)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <p className="mx-auto max-w-7xl px-5 py-20 text-muted-foreground">Chargement…</p>;
  }

  if (error || !formation) {
    return (
      <Section title="Formation introuvable">
        <Link to="/formations" className="text-brand-slate hover:underline">
          ← Retour aux formations
        </Link>
      </Section>
    );
  }

  return (
    <div data-testid={`formation-detail-${slug}`}>
      <PageHero
        eyebrow={`${formation.niveau} · ${formation.duree}`}
        title={formation.nom}
        subtitle={formation.resume}
        image={IMAGES.workshop}
      >
        <Link
          to="/formations"
          data-testid="back-to-formations"
          className="inline-flex items-center gap-2 text-sm font-medium text-brand-cream/80 transition-colors hover:text-brand-sand"
        >
          <ArrowLeft className="h-4 w-4" /> Toutes les formations
        </Link>
      </PageHero>

      <Section>
        <div className="grid gap-8 lg:grid-cols-2">
          <Card>
            <div className="flex items-center gap-3 text-brand-sage-dark">
              <CheckCircle2 className="h-5 w-5" />
              <h3 className="font-heading text-xl font-bold text-brand-slate-dark">Objectifs</h3>
            </div>
            <ul className="mt-5 space-y-3 text-sm leading-relaxed text-muted-foreground">
              {formation.objectifs.map((o) => (
                <li key={o} className="flex gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-sand-dark" />
                  {o}
                </li>
              ))}
            </ul>
          </Card>

          <Card>
            <div className="flex items-center gap-3 text-brand-sage-dark">
              <GraduationCap className="h-5 w-5" />
              <h3 className="font-heading text-xl font-bold text-brand-slate-dark">Compétences développées</h3>
            </div>
            <ul className="mt-5 space-y-3 text-sm leading-relaxed text-muted-foreground">
              {formation.competences.map((o) => (
                <li key={o} className="flex gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-sand-dark" />
                  {o}
                </li>
              ))}
            </ul>
          </Card>

          <Card>
            <div className="flex items-center gap-3 text-brand-sage-dark">
              <Briefcase className="h-5 w-5" />
              <h3 className="font-heading text-xl font-bold text-brand-slate-dark">Débouchés professionnels</h3>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {formation.debouches.map((d) => (
                <span
                  key={d}
                  className="rounded-full bg-brand-sage/15 px-3 py-1.5 text-xs font-medium text-brand-sage-dark"
                >
                  {d}
                </span>
              ))}
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-3 text-brand-sage-dark">
              <Building2 className="h-5 w-5" />
              <h3 className="font-heading text-xl font-bold text-brand-slate-dark">Poursuites d'études</h3>
            </div>
            <ul className="mt-5 space-y-3 text-sm leading-relaxed text-muted-foreground">
              {formation.poursuites.map((p) => (
                <li key={p} className="flex gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-sand-dark" />
                  {p}
                </li>
              ))}
            </ul>
          </Card>
        </div>

        <div className="mt-10 rounded-3xl bg-brand-sand-wrapper/60 p-8 lg:p-12">
          <h3 className="font-heading text-2xl font-bold text-brand-slate-dark">
            Stages & partenaires
          </h3>
          <p className="mt-4 max-w-3xl text-base text-muted-foreground">{formation.partenaires}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/contact?type=inscription"
              data-testid="formation-cta-inscription"
              className="inline-flex items-center gap-2 rounded-full bg-brand-slate px-6 py-3.5 text-sm font-semibold text-brand-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-slate-dark"
            >
              Demander un dossier d'inscription
            </Link>
            <Link
              to="/contact"
              data-testid="formation-cta-contact"
              className="inline-flex items-center gap-2 rounded-full border border-brand-slate/20 px-6 py-3.5 text-sm font-semibold text-brand-slate-dark transition-all duration-300 hover:bg-brand-slate hover:text-brand-cream"
            >
              Poser une question
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
